import React, { useState, useEffect, useMemo } from 'react'; // useMemo eklendi
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// FiList, FiX, FiChevronUp, FiChevronDown eklendi
import { FiSearch, FiDownload, FiShare2, FiList, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import './Dashboard.css';
import Sidebar from '../../components/Sidebar/Sidebar';
import ShareLinkModal from '../ShareLinkModal/ShareLinkModal';
import jsPDF from 'jspdf';
import { useToast } from '../../context/ToastContext/ToastContext';
import { getCompanyStageLabel, isDcfOnlyCompanyStage } from '../sectorOptions';

const Dashboard = () => {
    const { t, i18n } = useTranslation();
    const [reports, setReports] = useState([]);
    const [draft, setDraft] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sharedLinks, setSharedLinks] = useState([]);
    const [berkusRates, setBerkusRates] = useState({ '$': 1 });
    const [showShareModal, setShowShareModal] = useState(false);
      const { addToast } = useToast();
    // YENİ STATE'LER: Liste Modalı ve Sıralama
    const [showListModal, setShowListModal] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

    const navigate = useNavigate();

    useEffect(() => {
        fetchUserReports();
        fetchCurrentDraft();
        fetchSharedLinks();
    }, []);

    useEffect(() => {
        let isCurrent = true;
        fetch('https://api.exchangerate-api.com/v4/latest/EUR')
            .then(response => {
                if (!response.ok) throw new Error('Döviz kuru alınamadı');
                return response.json();
            })
            .then(data => {
                const usdRate = data.rates?.USD;
                const tryRate = data.rates?.TRY;
                if (isCurrent && Number.isFinite(usdRate) && usdRate > 0 && Number.isFinite(tryRate)) {
                    setBerkusRates({ '$': 1, '€': 1 / usdRate, '₺': tryRate / usdRate });
                }
            })
            .catch(error => console.error('Berkus değerlemesi için döviz kuru alınamadı:', error));

        return () => { isCurrent = false; };
    }, []);

    const fetchUserReports = async () => {
        try {
            const token = localStorage.getItem('userToken');
            const response = await fetch('/api/reports/user', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setReports(data);
            } else {
                const errorText = await response.text();
                console.error('API Error:', response.status, errorText);
            }
        } catch (error) {
            console.error('Fetch error:', error);
        }
    };

    const fetchCurrentDraft = async () => {
        try {
            const token = localStorage.getItem('userToken');
            const response = await fetch('/api/drafts/current', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.exists) {
                    setDraft(data.draft);
                }
            }
        } catch (error) {
            console.error('Draft fetch error:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSharedLinks = async () => {
        try {
            const token = localStorage.getItem('userToken');
            const response = await fetch('/api/shared-links/my-links', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setSharedLinks(data);
            }
        } catch (error) {
            console.error('Shared links fetch error:', error);
        }
    };

    const formatDate = (dateString) => {
        const locale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
        return new Date(dateString).toLocaleDateString(locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const replaceTurkishChars = (text) => {
        if (!text) return "";
        const charMap = {
            'ğ': 'g', 'Ğ': 'G', 'ü': 'u', 'Ü': 'U', 'ş': 's', 'Ş': 'S',
            'ı': 'i', 'İ': 'I', 'ö': 'o', 'Ö': 'O', 'ç': 'c', 'Ç': 'C'
        };
        return String(text).replace(/[ğĞüÜşŞıİöÖçÇ]/g, (match) => charMap[match]);
    };

    // 3. PDF OLUŞTURMA FONKSİYONU (DİL DESTEKLİ)
    const handleDownloadListPDF = () => {
        const doc = new jsPDF();

        // Şu anki dili kontrol et
        const locale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
        const isEnglish = i18n.language?.startsWith('en');

        // Başlık ve Tarih (Dile göre)
        const titleText = replaceTurkishChars(t('dashboard.pdfTitle', 'Tum Raporlar Listesi')); // Varsayılan: TR
        const dateLabel = replaceTurkishChars(t('dashboard.pdfCreatedDate', 'Olusturulma Tarihi'));
        const dateValue = new Date().toLocaleDateString(locale);

        // Sayfa ve renk ayarları
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const marginX = 14;
        const tableRight = pageWidth - marginX;
        const navy = [34, 31, 58];
        const green = [43, 213, 45];
        const rowHeight = 9;

        // Üst bant: logo yazısı, başlık ve tarih
        doc.setFillColor(...navy);
        doc.rect(0, 0, pageWidth, 32, 'F');
        doc.setFont(undefined, 'bold');
        doc.setFontSize(18);
        doc.setTextColor(255, 255, 255);
        doc.text('seed', marginX, 14);
        const seedWidth = doc.getTextWidth('seed');
        doc.setTextColor(...green);
        doc.text('X', marginX + seedWidth, 14);
        doc.setTextColor(255, 255, 255);
        doc.text('pert', marginX + seedWidth + doc.getTextWidth('X'), 14);

        doc.setFontSize(12);
        doc.text(titleText, marginX, 24);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
        doc.setTextColor(200, 200, 215);
        doc.text(`${dateLabel}: ${dateValue}`, tableRight, 24, { align: 'right' });

        // Özet satırı
        doc.setFontSize(10);
        doc.setTextColor(90, 90, 110);
        doc.text(
            isEnglish ? `Total reports: ${sortedReports.length}` : `Toplam rapor: ${sortedReports.length}`,
            marginX, 42
        );
        doc.setFillColor(...green);
        doc.rect(marginX, 45, 18, 0.8, 'F');

        // Sütun Konumları (tutarlar sağa hizalı)
        const colDate = marginX + 3;
        const colCompany = 48;
        const colDCFRight = 150;
        const colBerkusRight = tableRight - 3;

        // Başlıkları Çevir ve Temizle
        const hDate = replaceTurkishChars(t('dashboard.date', 'Tarih')).replace(/:\s*$/, '');
        const hCompany = replaceTurkishChars(t('dashboard.companyName', 'Sirket Adi'));
        const hDCF = isEnglish ? 'DCF Valuation' : 'DCF Degerlemesi';
        const hBerkus = isEnglish ? 'Berkus Valuation' : 'Berkus Degerlemesi';

        const drawTableHeader = (headerY) => {
            doc.setFillColor(...navy);
            doc.rect(marginX, headerY, tableRight - marginX, rowHeight + 1, 'F');
            doc.setFont(undefined, 'bold');
            doc.setFontSize(10);
            doc.setTextColor(255, 255, 255);
            const textY = headerY + 6.5;
            doc.text(hDate, colDate, textY);
            doc.text(hCompany, colCompany, textY);
            doc.text(hDCF, colDCFRight, textY, { align: 'right' });
            doc.text(hBerkus, colBerkusRight, textY, { align: 'right' });
            doc.setFont(undefined, 'normal');
            return headerY + rowHeight + 1;
        };

        let y = drawTableHeader(52);
        let rowIndex = 0;

        sortedReports.forEach((report) => {
            // Tarih Formatı (Locale'e göre)
            const date = new Date(report.createdAt).toLocaleDateString(locale);
            
            // Şirket Adı (Temizle ve Kısalt)
            let company = replaceTurkishChars(report.entrepreneurInfo?.companyName || '-');
            if (company.length > 30) company = company.substring(0, 27) + "...";

            // Para Birimi ve Değer Formatı
            let currency = report.entrepreneurInfo?.valuationCurrency || '$';
            if (currency === '₺') currency = 'TL';
            const valMetric = report.financialResults?.valuationMetric;
            
            let dcfVal = '-';
            if (valMetric) {
                // Sayı formatı (TR için 1.000,00 - EN için 1,000.00)
                dcfVal = `${new Intl.NumberFormat(locale).format(Math.round(valMetric))} ${currency}`;
            }
            
            const berkusRate = berkusRates[report.entrepreneurInfo?.valuationCurrency || '$'];
            const berkusVal = isDcfOnlyCompanyStage(report.entrepreneurInfo?.stage) || !Number.isFinite(berkusRate) || !report.totalScore
                ? '-'
                : `${new Intl.NumberFormat(locale).format(Math.round(report.totalScore * berkusRate))} ${currency}`;

            // Sayfa Sonu Kontrolü: yeni sayfada tablo başlığını tekrarla
            if (y + rowHeight > pageHeight - 18) {
                doc.addPage();
                y = drawTableHeader(16);
            }

            // Satır arka planı (zebra) ve alt çizgi
            if (rowIndex % 2 === 1) {
                doc.setFillColor(245, 247, 250);
                doc.rect(marginX, y, tableRight - marginX, rowHeight, 'F');
            }
            doc.setDrawColor(226, 232, 240);
            doc.line(marginX, y + rowHeight, tableRight, y + rowHeight);

            // Satırı Yaz
            const textY = y + 6;
            doc.setFontSize(9.5);
            doc.setTextColor(90, 90, 110);
            doc.text(date, colDate, textY);
            doc.setTextColor(30, 30, 45);
            doc.setFont(undefined, 'bold');
            doc.text(company, colCompany, textY);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(46, 125, 50);
            doc.text(dcfVal, colDCFRight, textY, { align: 'right' });
            doc.text(berkusVal, colBerkusRight, textY, { align: 'right' });

            y += rowHeight;
            rowIndex += 1;
        });

        // Alt bilgi: tüm sayfalara marka ve sayfa numarası
        const totalPages = doc.getNumberOfPages();
        for (let page = 1; page <= totalPages; page++) {
            doc.setPage(page);
            doc.setDrawColor(226, 232, 240);
            doc.line(marginX, pageHeight - 12, tableRight, pageHeight - 12);
            doc.setFontSize(8);
            doc.setTextColor(140, 140, 160);
            doc.text(`SeedXpert © ${new Date().getFullYear()}`, marginX, pageHeight - 7);
            doc.text(
                isEnglish ? `Page ${page} / ${totalPages}` : `Sayfa ${page} / ${totalPages}`,
                tableRight, pageHeight - 7, { align: 'right' }
            );
        }

        // Dosya Adı (Tarihli ve Dilli)
        const fileName = i18n.language === 'tr' 
            ? `Rapor_Listesi_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '_')}.pdf`
            : `Report_List_${new Date().toLocaleDateString('en-US').replace(/\//g, '_')}.pdf`;

        doc.save(fileName);
    };
    const formatNumber = (num) => {
        const locale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
        return new Intl.NumberFormat(locale).format(Math.round(num || 0));
    };

    const formatBerkusValuation = (report) => {
        const currency = report.entrepreneurInfo?.valuationCurrency || '$';
        const rate = berkusRates[currency];
        // DCF ile aynı: puan yoksa veya 0 ise boş göster
        if (!Number.isFinite(rate) || !report.totalScore) return '—';
        return `${formatNumber(report.totalScore * rate)} ${currency}`;
    };

    const formatDcfValuation = (report) => {
        const value = report.financialResults?.valuationMetric;
        // Liste görünümüyle aynı: değer yoksa veya 0 ise boş göster
        if (!Number.isFinite(value) || value === 0) return '—';
        return `${formatNumber(value)} ${report.entrepreneurInfo?.valuationCurrency || '$'}`;
    };

    const getScoreColor = (percentage) => {
        if (percentage >= 75) return '#28a746c5';
        if (percentage >= 50) return '#f9be0cb1';
        return '#ba2e3cb7';
    };

    const getStepDisplayName = (stepName) => {
        const stepNames = {
            'entrepreneur-info': t('dashboard.step1'),
            'financial-table': t('dashboard.step2'),
            'assessment': t('dashboard.step3')
        };
        return stepNames[stepName] || stepName;
    };

    const continueDraft = () => {
        if (draft) {
            navigate(`/${draft.lastActiveStep}`);
        }
    };

    const startNewReport = () => {
        navigate('/entrepreneur-info');
    };

    const handleCopyLink = (token) => {
        const linkUrl = `${window.location.origin}/shared-form/${token}`;
        navigator.clipboard.writeText(linkUrl);
         addToast(t('dashboard.linkCopied'), 'success');
    };

    // --- SIRALAMA MANTIĞI ---
    const handleSort = (key) => {
        let direction = 'desc';
        // Eğer aynı kolona tekrar tıklanırsa yönü değiştir
        if (sortConfig.key === key && sortConfig.direction === 'desc') {
            direction = 'asc';
        }
        setSortConfig({ key, direction });
    };

    // Raporları sıralama (useMemo ile performans optimizasyonu)
    const sortedReports = useMemo(() => {
        // Orijinal diziyi bozmamak için kopyasını alalım
        let sortableReports = [...reports];

        // Önce arama filtresini uygula (varsa)
        if (searchTerm) {
            sortableReports = sortableReports.filter(report =>
                report.entrepreneurInfo?.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                report.entrepreneurInfo?.sector?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        sortableReports.sort((a, b) => {
            let aValue, bValue;

            switch (sortConfig.key) {
                case 'date':
                    aValue = new Date(a.createdAt);
                    bValue = new Date(b.createdAt);
                    break;
                case 'dcf':
                    // DCF değeri yoksa 0 kabul et
                    aValue = a.financialResults?.valuationMetric || 0;
                    bValue = b.financialResults?.valuationMetric || 0;
                    break;
                case 'berkus':
                    // Berkus tutarı USD olarak saklanıyor; para biriminden bağımsız karşılaştırma için ham değeri kullan
                    aValue = a.totalScore || 0;
                    bValue = b.totalScore || 0;
                    break;
                case 'name':
                    aValue = (a.entrepreneurInfo?.companyName || '').toLowerCase();
                    bValue = (b.entrepreneurInfo?.companyName || '').toLowerCase();
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });

        return sortableReports;
    }, [reports, sortConfig, searchTerm]);

    // Sıralama ikonu gösterme yardımcısı
    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) return <div className="sort-icon-placeholder" />;
        return sortConfig.direction === 'asc' ? <FiChevronUp /> : <FiChevronDown />;
    };

    // Kart görünümü için filtrelenmiş liste (Dashboard ana ekranı için)
    const filteredReportsForGrid = reports.filter(report =>
        report.entrepreneurInfo?.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.entrepreneurInfo?.sector?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="dashboard-container">
                <Sidebar />
                <div className="dashboard-content">
                    <div className="loading">
                        <div className="loading-spinner"></div>
                        <p>{t('dashboard.loadingData')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            <Sidebar />
            <div className="dashboard-content">
                <div className="dashboard-header">
                    <div className="header-left">
                        <h1>{t('dashboard.panel')}</h1>
                        <p className="subtitle">
                            {t('dashboard.subtitle')}
                        </p>
                    </div>
                    <div className="header-right">
                        {/* LİSTE GÖRÜNÜMÜ BUTONU EKLENDİ */}
                        <button 
                            className="list-view-btn"
                            onClick={() => setShowListModal(true)}
                            title="Liste Görünümü"
                        >
                            <FiList /> {t('dashboard.listView') || "Liste Görünümü"}
                        </button>

                        <button
                            className="share-link-btn"
                            onClick={() => setShowShareModal(true)}
                        >
                            <FiShare2 />
                            {t('dashboard.shareFormLink')}
                        </button>
                        <button
                            className="new-report-btn"
                            onClick={startNewReport}
                        >
                            <span className="btn-icon">+</span>
                            {t('dashboard.newAssessmentBtn')}
                        </button>
                    </div>
                </div>

                <ShareLinkModal
                    isOpen={showShareModal}
                    onClose={() => setShowShareModal(false)}
                    onLinkCreated={fetchSharedLinks}
                />
                
                {/* --- LİSTE GÖRÜNÜMÜ POPUP (MODAL) --- */}
                {showListModal && (
                    <div className="modal-overlay list-modal-overlay" onClick={() => setShowListModal(false)}>
                        <div className="modal-content list-modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <h2>{t('dashboard.allReports')}</h2> {/* Çeviri anahtarı kullanıldı */}
                
                <button 
                    className="pdf-list-export-btn"
                    onClick={handleDownloadListPDF}
                    title={t('dashboard.downloadPdfTooltip', 'Listeyi PDF olarak indir')}
                >
                    <FiDownload /> {t('dashboard.downloadPdf', 'PDF İndir')}
                </button>
            </div>

            <button className="modal-close-btn" onClick={() => setShowListModal(false)}>
                <span aria-hidden="true">×</span>
            </button>
        </div>
                            
                            <div className="modal-body-scroll">
                                {sortedReports.length > 0 ? (
                                    <table className="report-list-table">
                                        <thead>
                                            <tr>
                                                <th onClick={() => handleSort('date')}>
                                                    <div className="th-content">
                                                        {t('dashboard.date') || "Tarih"} {getSortIcon('date')}
                                                    </div>
                                                </th>
                                                <th onClick={() => handleSort('name')}>
                                                    <div className="th-content">
                                                        {t('dashboard.companyName') || "Girişim Adı"} {getSortIcon('name')}
                                                    </div>
                                                </th>
                                                <th onClick={() => handleSort('dcf')}>
                                                    <div className="th-content">
                                                        DCF Değerleme {getSortIcon('dcf')}
                                                    </div>
                                                </th>
                                                <th onClick={() => handleSort('berkus')}>
                                                    <div className="th-content">
                                                        {i18n.language?.startsWith('en') ? 'Berkus Valuation' : 'Berkus Değerlemesi'} {getSortIcon('berkus')}
                                                    </div>
                                                </th>
                                                <th>{t('dashboard.actions') || "İşlem"}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedReports.map((report) => (
                                                <tr key={report._id}>
                                                    <td>{formatDate(report.createdAt)}</td>
                                                    <td style={{fontWeight: '600'}}>{report.entrepreneurInfo?.companyName || '-'}</td>
                                                    <td style={{color: '#2e7d32', fontWeight: 'bold'}}>
                                                        {report.financialResults?.valuationMetric 
                                                            ? `${formatNumber(report.financialResults.valuationMetric)} ${report.entrepreneurInfo?.valuationCurrency || '$'}`
                                                            : '-'}
                                                    </td>
                                                    <td style={{color: '#2e7d32', fontWeight: 'bold'}}>
                                                        {isDcfOnlyCompanyStage(report.entrepreneurInfo?.stage) ? '—' : formatBerkusValuation(report)}
                                                    </td>
                                                    <td>
                                                        <button 
                                                            className="list-view-btn-small"
                                                            onClick={() => {
                                                                navigate(`/dashboard/report/${report._id}`);
                                                                setShowListModal(false);
                                                            }}
                                                        >
                                                            {t('dashboard.view') || "Görüntüle"}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div className="no-data-message">
                                        Rapor bulunamadı.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* ... (Shared Links Section - Aynı Kalacak) ... */}
                {sharedLinks.length > 0 && (
                    <div className="shared-links-section">
                        <div className="section-header">
                            {t('dashboard.section-header')}
                            <span className="link-count">
                                {t('dashboard.activeLinkCount', { count: sharedLinks.length })}
                            </span>
                        </div>
                        {/* ... Link Grid ... */}
                        <div className="links-grid">
                            {sharedLinks.map(link => (
                                <div key={link._id} className="link-card">
                                    <div className="link-card-header">
                                        <h3>{link.linkName}</h3>
                                    </div>

                                    <div className="link-stats">
                                        <div className="stat">
                                            <span className="stat-value">{link.viewCount || 0}</span>
                                            <span className="stat-label">{t('dashboard.views')}</span>
                                        </div>
                                        <div className="stat">
                                            <span className="stat-value">{link.submissionCount || 0}</span>
                                            <span className="stat-label">{t('dashboard.submissions')}</span>
                                        </div>
                                        {link.maxSubmissions && (
                                            <div className="stat">
                                                <span className="stat-value">{link.maxSubmissions}</span>
                                                <span className="stat-label">{t('dashboard.maximum')}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="link-dates">
                                        <small>{t('dashboard.created')} {formatDate(link.createdAt)}</small>
                                        <small>{t('dashboard.expires')} {formatDate(link.expiresAt)}</small>
                                    </div>

                                    <div className="link-actions">
                                        <button 
                                            className="copy-link-btn"
                                            onClick={() => handleCopyLink(link.token)}
                                        >
                                            {t('dashboard.copyLink')}
                                        </button>
                                        
                                        {link.submissions && link.submissions.length > 0 && (
                                            <button 
                                                className="view-submissions-btn"
                                                onClick={() => navigate(`/link-submissions/${link._id}`)}
                                            >
                                                {t('dashboard.viewSubmissions')} ({link.submissions.length})
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ... (Draft Section - Aynı Kalacak) ... */}
                {draft && (
                    <div className="draft-section">
                        {/* ... Draft Card İçeriği ... */}
                        <div className="draft-card">
                             <div className="draft-header">
                                <div className="draft-info">
                                    <h3>{t('dashboard.incompleteDraft')}</h3>
                                    <p className="draft-company">
                                        {draft.entrepreneurInfo?.companyName || t('dashboard.companyNotSpecified')}
                                    </p>
                                </div>
                                <div className="draft-progress">
                                    <div className="progress-circle">
                                        <svg viewBox="0 0 36 36" className="circular-chart">
                                            <path 
                                                className="circle-bg"
                                                d="M18 2.0845
                                                   a 15.9155 15.9155 0 0 1 0 31.831
                                                   a 15.9155 15.9155 0 0 1 0 -31.831"
                                            />
                                            <path 
                                                className="circle"
                                                strokeDasharray={`${draft.completionPercentage}, 100`}
                                                d="M18 2.0845
                                                   a 15.9155 15.9155 0 0 1 0 31.831
                                                   a 15.9155 15.9155 0 0 1 0 -31.831"
                                            />
                                        </svg>
                                        <div className="percentage">{draft.completionPercentage}%</div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="draft-body">
                                <div className="step-indicators">
                                    <div className={`step-indicator ${draft.stepCompletion.entrepreneurInfo ? 'completed' : ''}`}>
                                        <span className="step-number">1</span>
                                        <span className="step-name">{t('dashboard.step1')}</span>
                                    </div>
                                    <div className={`step-indicator ${draft.stepCompletion.financialTable ? 'completed' : ''}`}>
                                        <span className="step-number">2</span>
                                        <span className="step-name">{t('dashboard.step2')}</span>
                                    </div>
                                    <div className={`step-indicator ${draft.stepCompletion.assessment ? 'completed' : ''}`}>
                                        <span className="step-number">3</span>
                                        <span className="step-name">{t('dashboard.step3')}</span>
                                    </div>
                                </div>

                                <div className="draft-stats">
                                    <div className="stat-item">
                                        <span className="stat-label">{t('dashboard.lastUpdate')}</span>
                                        <span className="stat-value">{formatDate(draft.updatedAt)}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">{t('dashboard.nextStep')}</span>
                                        <span className="stat-value">{getStepDisplayName(draft.lastActiveStep)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="draft-footer">
                                <button className="continue-btn" onClick={continueDraft}>
                                    {t('dashboard.continueFromWhere')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tamamlanan Raporlar Bölümü (Kartlar) - Aynı Kalacak */}
                <div className="reports-section">
                    <div className="section-header">
                        <div className="section-header-left">
                            <span className="report-count">
                                {reports.length} {t('dashboard.reportsCount')}
                            </span>
                            <h2>{t('dashboard.completedReports')}</h2>
                        </div>
                        {reports.length > 0 && (
                            <div className="search-box">
                                <input
                                    type="text"
                                    placeholder={t('dashboard.searchPlaceholder')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="search-input"
                                />
                                <FiSearch className="search-icon" />
                            </div>
                        )}
                    </div>

                    {filteredReportsForGrid.length === 0 ? (
                        <div className="no-reports">
                            {reports.length === 0 ? (
                                <>
                                    <div className="no-reports-icon">📊</div>
                                    <h3>{t('dashboard.noReportsYet')}</h3>
                                    <p>{t('dashboard.noReportsDesc')}</p>
                                    <button
                                        className="start-first-btn"
                                        onClick={startNewReport}
                                    >
                                        {t('dashboard.startFirstAssessment')}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <h3>{t('dashboard.noSearchResults')}</h3>
                                    <p>"{searchTerm}" {t('dashboard.noResultsFor')}</p>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="reports-grid">
                            {filteredReportsForGrid.map(report => (
                                <div key={report._id} className="report-card">
                                    <div className="card-header">
                                        <h3 className="company-name">
                                            {report.entrepreneurInfo?.companyName || t('dashboard.companyName')}
                                        </h3>
                                        {!isDcfOnlyCompanyStage(report.entrepreneurInfo?.stage) && <div
                                            className="score-badge"
                                            style={{
                                                backgroundColor: getScoreColor(report.overallPercentage),
                                                color: 'white'
                                            }}
                                        >
                                            {(report.overallPercentage || 0).toFixed(0)}%
                                        </div>}
                                    </div>

                                    <div className="card-body">
                                        <div className="report-details">
                                            <div className="detail-item">
                                                <span className="label">{t('dashboard.sector')}</span>
                                                <span className="value">
                                                    {report.entrepreneurInfo?.sector || t('dashboard.notSpecified')}
                                                </span>
                                            </div>
                                            <div className="detail-item">
                                                <span className="label">{i18n.language?.startsWith('en') ? 'Company Stage' : 'Şirket Aşaması'}</span>
                                                <span className="value">
                                                    {getCompanyStageLabel(report.entrepreneurInfo?.stage, i18n.language) || t('dashboard.notSpecified')}
                                                </span>
                                            </div>
                                            <div className="detail-item">
                                                <span className="label">{t('dashboard.date')}</span>
                                                <span className="value">
                                                    {formatDate(report.createdAt)}
                                                </span>
                                            </div>
                                            {!isDcfOnlyCompanyStage(report.entrepreneurInfo?.stage) && <div className="detail-item">
                                                <span className="label">{i18n.language === 'en' ? 'Berkus Valuation' : 'Berkus Değerlemesi'}</span>
                                                <span className="value score">
                                                    {formatBerkusValuation(report)}
                                                </span>
                                            </div>}
                                            <div className="detail-item">
                                                <span className="label">{i18n.language?.startsWith('en') ? 'DCF Valuation' : 'DCF Değerlemesi'}</span>
                                                <span className="value score">
                                                    {formatDcfValuation(report)}
                                                </span>
                                            </div>
                                        </div>

                                        {!isDcfOnlyCompanyStage(report.entrepreneurInfo?.stage) && <div className="progress-section">
                                            <div className="progress-label">
                                                <span>{t('dashboard.overallSuccess')}</span>
                                                <span>{(report.overallPercentage || 0).toFixed(1)}%</span>
                                            </div>
                                            <div className="progress-bar">
                                                <div
                                                    className="progress-fill"
                                                    style={{
                                                        width: `${report.overallPercentage || 0}%`,
                                                        backgroundColor: getScoreColor(report.overallPercentage)
                                                    }}
                                                ></div>
                                            </div>
                                        </div>}
                                    </div>

                                    <div className="card-footer">
                                        <button
                                            className="view-report-btn"
                                            onClick={() => navigate(`/dashboard/report/${report._id}`)}
                                        >
                                            {t('dashboard.viewReport')}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
